<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import QRCode from 'qrcode';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { getChannelChatLinkUrl } from 'dashboard/helper/channelChatLink';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  /** When false, only the chat link block is shown (e.g. on main inbox settings to avoid duplicating the inbox name field). */
  showChannelName: {
    type: Boolean,
    default: true,
  },
});

const { t } = useI18n();
const store = useStore();
const inboxUiFlags = useMapGetter('inboxes/getUIFlags');

const localName = ref('');
const qrDataUrl = ref('');
const isGeneratingQr = ref(false);
const qrDialogRef = ref(null);

watch(
  () => props.inbox?.id,
  () => {
    localName.value = props.inbox?.name || '';
    qrDataUrl.value = '';
    qrDialogRef.value?.close();
  },
  { immediate: true }
);

watch(
  () => props.inbox?.name,
  name => {
    localName.value = name || '';
  }
);

const chatLinkUrl = computed(() => getChannelChatLinkUrl(props.inbox));

const isUpdating = computed(() => inboxUiFlags.value?.isUpdating);

const nameDirty = computed(
  () => localName.value?.trim() !== (props.inbox?.name || '').trim()
);

async function copyLink() {
  const url = chatLinkUrl.value;
  if (!url) return;
  try {
    await copyTextToClipboard(url);
    useAlert(t('INBOX_MGMT.CONFIGURATION_IDENTITY.CHAT_LINK.COPY_SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
}

function onQrDialogClose() {
  qrDataUrl.value = '';
  isGeneratingQr.value = false;
}

async function openQrModal() {
  const url = chatLinkUrl.value;
  if (!url) return;
  qrDataUrl.value = '';
  qrDialogRef.value?.open();
  isGeneratingQr.value = true;
  try {
    qrDataUrl.value = await QRCode.toDataURL(url);
  } catch {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
    qrDialogRef.value?.close();
  } finally {
    isGeneratingQr.value = false;
  }
}

async function saveChannelName() {
  const name = localName.value?.trim();
  if (!name) {
    useAlert(
      t('INBOX_MGMT.CONFIGURATION_IDENTITY.CHANNEL_NAME.ERROR_REQUIRED')
    );
    return;
  }
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      name,
      formData: false,
    });
    useAlert(t('INBOX_MGMT.CONFIGURATION_IDENTITY.CHANNEL_NAME.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
}
</script>

<template>
  <div v-show="chatLinkUrl || showChannelName" class="mb-4 flex flex-col gap-2">
    <SettingsFieldSection
      v-if="chatLinkUrl"
      stacked
      :label="$t('INBOX_MGMT.CONFIGURATION_IDENTITY.CHAT_LINK.LABEL')"
    >
      <div class="flex flex-wrap items-center gap-2">
        <div class="min-w-0 flex-1 basis-48">
          <Input
            type="text"
            :model-value="chatLinkUrl"
            readonly
            custom-input-class="truncate font-mono text-sm !bg-n-alpha-2 cursor-default"
          />
        </div>
        <button
          type="button"
          class="inline-flex h-8 shrink-0 items-center justify-center rounded-lg px-2 text-n-slate-11 outline outline-1 outline-offset-[-1px] outline-n-weak transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-n-blue-9"
          :aria-label="
            $t('INBOX_MGMT.CONFIGURATION_IDENTITY.CHAT_LINK.COPY_ARIA')
          "
          @click="copyLink"
        >
          <Icon icon="i-lucide-copy" class="size-4" />
        </button>
        <button
          type="button"
          class="shrink-0 text-sm font-medium text-n-blue-11 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="isGeneratingQr"
          @click="openQrModal"
        >
          {{ $t('INBOX_MGMT.CONFIGURATION_IDENTITY.CHAT_LINK.GENERATE_QR') }}
        </button>
      </div>
    </SettingsFieldSection>

    <SettingsFieldSection
      v-if="showChannelName"
      stacked
      :label="$t('INBOX_MGMT.CONFIGURATION_IDENTITY.CHANNEL_NAME.LABEL')"
      :help-text="$t('INBOX_MGMT.CONFIGURATION_IDENTITY.CHANNEL_NAME.HELP')"
    >
      <div class="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div class="min-w-0 flex-1">
          <input
            v-model="localName"
            type="text"
            class="block w-full border-0 border-b border-n-weak bg-transparent px-0.5 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 transition-colors focus:border-n-brand focus:outline-none focus:ring-0 dark:placeholder:text-n-slate-10"
            :placeholder="
              $t('INBOX_MGMT.CONFIGURATION_IDENTITY.CHANNEL_NAME.PLACEHOLDER')
            "
          />
        </div>
        <NextButton
          :label="$t('INBOX_MGMT.CONFIGURATION_IDENTITY.CHANNEL_NAME.SAVE')"
          :disabled="!nameDirty"
          :is-loading="isUpdating"
          class="sm:shrink-0"
          @click="saveChannelName"
        />
      </div>
    </SettingsFieldSection>

    <Dialog
      ref="qrDialogRef"
      type="edit"
      width="sm"
      :title="$t('INBOX_MGMT.CONFIGURATION_IDENTITY.CHAT_LINK.QR_MODAL_TITLE')"
      :show-confirm-button="false"
      :cancel-button-label="
        $t('INBOX_MGMT.CONFIGURATION_IDENTITY.CHAT_LINK.QR_MODAL_CLOSE')
      "
      @close="onQrDialogClose"
    >
      <div
        class="flex min-h-48 flex-col items-center justify-center gap-4 py-2"
      >
        <SpinnerLoader v-if="isGeneratingQr" :size="32" class="text-n-blue-9" />
        <div
          v-else-if="qrDataUrl"
          class="rounded-lg outline outline-1 outline-n-strong outline-offset-[-1px]"
        >
          <img
            :src="qrDataUrl"
            :alt="$t('INBOX_MGMT.CONFIGURATION_IDENTITY.CHAT_LINK.QR_ALT')"
            class="size-48 rounded-lg dark:invert"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>
